import {useKeyPress} from '@github-ui/use-key-press'
import {FormControl, TextInput, useConfirm} from '@primer/react'
import type {ChangeEvent} from 'react'
import type React from 'react'

import {HOTKEYS, LABELS, TEST_IDS} from '../constants'

type IssueTitleInputProps = {
  titleRef: React.RefObject<HTMLInputElement>
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  commitIssueTitleEdit: () => void
  cancelIssueTitleEdit: () => void
  isDirty: boolean
  validationError?: string
  isSubmitting: boolean
}

export function IssueTitleInput({
  titleRef,
  value,
  onChange,
  commitIssueTitleEdit,
  cancelIssueTitleEdit,
  isDirty,
  validationError,
  isSubmitting,
}: IssueTitleInputProps) {
  const confirm = useConfirm()

  useKeyPress(
    [HOTKEYS.closeEdit],
    async (event: KeyboardEvent) => {
      event.preventDefault()
      if (isDirty) {
        // Prompt user to acknowledge they want to exit without saving
        const discardChanges = await confirm({
          title: LABELS.unsavedChangesTitle,
          content: LABELS.unsavedChangesContent,
          confirmButtonType: 'danger',
        })
        if (!discardChanges) {
          return
        }
      }
      cancelIssueTitleEdit()
    },
    {triggerWhenInputElementHasFocus: true},
  )

  useKeyPress(
    [HOTKEYS.commitEdit],
    (event: KeyboardEvent) => {
      event.preventDefault()
      if (isDirty && value.length > 0) {
        commitIssueTitleEdit()
      } else {
        cancelIssueTitleEdit()
      }
    },
    {triggerWhenInputElementHasFocus: true, scopeRef: titleRef, ignoreModifierKeys: true},
  )

  return (
    <FormControl
      sx={{
        width: '100%',
      }}
    >
      <FormControl.Label visuallyHidden>Title input</FormControl.Label>
      <TextInput
        sx={{width: '100%'}}
        ref={titleRef}
        onChange={onChange}
        value={value}
        placeholder={LABELS.viewTitlePlaceholder}
        data-testid={TEST_IDS.issueTitleInput}
        disabled={isSubmitting}
      />
      {validationError && <FormControl.Validation variant="error">{validationError}</FormControl.Validation>}
    </FormControl>
  )
}

try{ IssueTitleInput.displayName ||= 'IssueTitleInput' } catch {}